import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Flask, Play } from "@phosphor-icons/react";

export default function SimulationLab() {
  const [processes, setProcesses] = useState([]);
  const [sims, setSims] = useState([]);
  const [name, setName] = useState("Automate + Parallelize");
  const [processId, setProcessId] = useState("");
  const [waitReduce, setWaitReduce] = useState([40]);
  const [addAutomation, setAddAutomation] = useState(true);
  const [parallelize, setParallelize] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, s] = await Promise.all([api.get("/processes"), api.get("/simulations")]);
      setProcesses(p.data); setSims(s.data);
      setProcessId(p.data[0]?.id || "");
    })();
  }, []);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/simulations/run", {
        name, process_id: processId,
        changes: {
          reduce_wait_time_pct: waitReduce[0],
          add_automation: addAutomation,
          parallelize_steps: parallelize,
        }
      });
      setResult(data);
      const list = await api.get("/simulations");
      setSims(list.data);
      toast.success("Simulation completed");
    } catch (e) {
      toast.error("Simulation failed");
    } finally { setBusy(false); }
  };

  const chartData = result ? [
    { metric: "Cycle (hrs)", current: result.current_metrics.avg_cycle_time_hours, proposed: result.proposed_metrics.avg_cycle_time_hours },
    { metric: "Throughput", current: result.current_metrics.throughput_per_day, proposed: result.proposed_metrics.throughput_per_day },
    { metric: "Error %", current: result.current_metrics.error_rate_pct, proposed: result.proposed_metrics.error_rate_pct },
    { metric: "SLA %", current: result.current_metrics.sla_compliance_pct, proposed: result.proposed_metrics.sla_compliance_pct },
  ] : [];

  return (
    <div className="space-y-6" data-testid="simulation-page">
      <div>
        <div className="data-label">What-If Sandbox</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Simulation Lab</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Model current-state vs proposed-state with quantified impact before you commit change.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Scenario builder */}
        <form onSubmit={run} className="itsme-card p-5 space-y-4 lg:col-span-1">
          <div className="flex items-center gap-2">
            <Flask size={18} className="text-[#00E5FF]" />
            <div className="font-heading text-lg font-semibold">Scenario Builder</div>
          </div>
          <div>
            <Label className="data-label">Scenario name</Label>
            <Input data-testid="sim-name" value={name} onChange={(e) => setName(e.target.value)}
                   className="mt-2 bg-[#0F1117] border-[#272A35]" />
          </div>
          <div>
            <Label className="data-label">Process</Label>
            <Select value={processId} onValueChange={setProcessId}>
              <SelectTrigger data-testid="sim-process" className="mt-2 bg-[#0F1117] border-[#272A35]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#13151A] border-[#272A35] text-slate-100">
                {processes.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="data-label">Reduce wait time</Label>
              <span className="text-sm font-mono text-[#00E5FF]">{waitReduce[0]}%</span>
            </div>
            <Slider data-testid="sim-wait" value={waitReduce} onValueChange={setWaitReduce} max={90} step={5} className="mt-3" />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-200">Add automation agent</Label>
            <Switch data-testid="sim-automation" checked={addAutomation} onCheckedChange={setAddAutomation} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-200">Parallelize steps</Label>
            <Switch data-testid="sim-parallel" checked={parallelize} onCheckedChange={setParallelize} />
          </div>

          <Button data-testid="sim-run" type="submit" disabled={busy || !processId}
                  className="w-full bg-[#0055FF] hover:bg-[#0044cc]">
            <Play size={14} weight="fill" className="mr-2" /> {busy ? "Running…" : "Run Simulation"}
          </Button>
        </form>

        {/* Result */}
        <div className="itsme-card p-5 lg:col-span-2">
          {result ? (
            <>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="data-label">Comparison</div>
                  <div className="font-heading text-lg font-semibold">{result.name}</div>
                  <div className="text-xs text-slate-400 mt-1">{result.process_name}</div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-right">
                  <ImpactMetric label="Cycle" value={result.impact.cycle_time_delta_pct} inverse />
                  <ImpactMetric label="Throughput" value={result.impact.throughput_delta_pct} />
                  <ImpactMetric label="SLA" value={result.impact.sla_delta_pct} suffix="pp" />
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#272A35" />
                    <XAxis dataKey="metric" stroke="#9CA3AF" fontSize={11} />
                    <YAxis stroke="#9CA3AF" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#13151A", border: "1px solid #272A35", borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="current" fill="#9CA3AF" name="Current" />
                    <Bar dataKey="proposed" fill="#00E5FF" name="Proposed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="h-72 flex flex-col items-center justify-center text-slate-500 text-sm">
              <Flask size={32} className="mb-3 text-slate-600" />
              Configure a scenario and click Run to compare outcomes.
            </div>
          )}
        </div>
      </div>

      {/* History */}
      <div className="itsme-card p-5">
        <div className="data-label">Recent Simulations</div>
        <div className="font-heading text-lg font-semibold mb-3">History</div>
        <div className="divide-y divide-[#272A35]">
          {sims.length === 0 && <div className="text-sm text-slate-500 py-4">No simulations yet.</div>}
          {sims.map((s) => (
            <div key={s.id} data-testid={`sim-item-${s.id}`}
                 className="py-3 flex items-center justify-between hover:bg-[#1C1F26] px-2 rounded transition-colors">
              <div>
                <div className="text-sm">{s.name}</div>
                <div className="text-xs text-slate-500">{s.process_name} · {new Date(s.created_at).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono text-[#00E5FF]">Δ throughput {s.impact.throughput_delta_pct}%</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">risk: {s.risk}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const ImpactMetric = ({ label, value, inverse, suffix = "%" }) => {
  const positive = inverse ? value < 0 : value > 0;
  return (
    <div>
      <div className="data-label text-right">{label}</div>
      <div className={`font-heading text-base font-semibold ${positive ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
        {value > 0 ? "+" : ""}{value}{suffix}
      </div>
    </div>
  );
};
